/**
 * Checkbox Cell — grid intersection cell for habit × day.
 * React.memo critical — 465+ cells per month (15 habits × 31 days).
 *
 * OPTIMIZED: No hooks inside. Toggle callback and data come from parent.
 *
 * States:
 *  - Not applicable (frequency mismatch): empty cell
 *  - Completed (past/today): green bg + green border + ✓
 *  - Missed (past, applicable, NOT completed): red bg + red border + ✗
 *  - Today / grace period: active checkbox (toggleable)
 *  - Future days: dimmed, disabled
 */

import React from 'react'
import { motion } from 'framer-motion'
import { habitAppliesToDate, formatDateStr, isDateToggleable } from '@turnifly/shared/utils'
import type { MonthlyHabitItem } from '@turnifly/shared/types'
import { triggerHapticImpact } from '@turnifly/shared/utils'

interface CheckboxCellProps {
  habit: MonthlyHabitItem
  day: number
  year: number
  month: number
  todayDateStr: string // YYYY-MM-DD
  onToggle: (habitId: string, logDate: string, completed: boolean) => void
}

export const CheckboxCell: React.FC<CheckboxCellProps> = React.memo(({
  habit, day, year, month, todayDateStr, onToggle,
}) => {
  const dateStr = formatDateStr(year, month, day)

  // Frequency check — no checkbox if habit doesn't apply on this day
  if (!habitAppliesToDate(habit, dateStr)) {
    return <div className="h-full w-full" />
  }

  const isCompleted = habit.logs.some((log) => log.log_date === dateStr && log.completed)
  const canToggle = isDateToggleable(dateStr, todayDateStr)
  const isToday = dateStr === todayDateStr
  const isFuture = dateStr > todayDateStr
  const isPast = !isToday && !isFuture && !canToggle
  const isMissed = isPast && !isCompleted

  const handleToggle = (): void => {
    if (!canToggle) return

    triggerHapticImpact(isCompleted ? 'light' : 'medium')

    onToggle(habit.id, dateStr, !isCompleted)
  }

  const opacityCls = isToday || canToggle ? '' : isFuture ? 'opacity-50' : ''

  // Box style based on state
  const boxCls = isCompleted
    ? 'bg-success/20 border border-success/50 shadow-[0_0_8px_rgba(34,197,94,0.12)]'
    : isMissed
      ? 'bg-danger/20 border border-danger/50'
      : 'border border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'

  return (
    <button
      onClick={handleToggle}
      disabled={!canToggle}
      className={`h-full w-full flex items-center justify-center tap-highlight-transparent transition-colors ${opacityCls} ${
        !canToggle ? 'cursor-not-allowed' : 'hover:bg-white/[0.04] active:bg-white/[0.08]'
      }`}
    >
      <div className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 ${boxCls}`}>
        {isCompleted && (
          <motion.svg
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 15 }}
            className="w-3.5 h-3.5 text-success"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <motion.path
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              d="M20 6L9 17l-5-5"
            />
          </motion.svg>
        )}
        {isMissed && (
          <svg
            className="w-3 h-3 text-danger"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        )}
      </div>
    </button>
  )
}, (prev, next) => {
  if (prev.day !== next.day || prev.year !== next.year || prev.month !== next.month) return false
  if (prev.todayDateStr !== next.todayDateStr) return false
  if (prev.onToggle !== next.onToggle) return false
  if (prev.habit.frequency !== next.habit.frequency) return false

  const dateStr = formatDateStr(prev.year, prev.month, prev.day)
  const prevCompleted = prev.habit.logs.some((l) => l.log_date === dateStr && l.completed)
  const nextCompleted = next.habit.logs.some((l) => l.log_date === dateStr && l.completed)
  return prevCompleted === nextCompleted
})

CheckboxCell.displayName = 'CheckboxCell'
